'use client';

import { useEffect, useState } from 'react';
import type Marquee from 'react-fast-marquee';

// Lazy load Marquee only when needed
let MarqueeComponent: typeof Marquee | null = null;

interface DynamicMarqueeProps {
  children: React.ReactNode;
  speed?: number;
  gradient?: boolean;
  gradientColor?: string;
  gradientWidth?: number | string;
  pauseOnHover?: boolean;
  pauseOnClick?: boolean;
  direction?: 'left' | 'right';
  play?: boolean;
  loop?: number;
  delay?: number;
  className?: string;
  style?: React.CSSProperties;
  autoFill?: boolean;
}

export default function DynamicMarquee(props: DynamicMarqueeProps) {
  const [Component, setComponent] = useState<typeof Marquee | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    // Use requestIdleCallback to defer loading until browser is idle
    const idleCallback =
      'requestIdleCallback' in window
        ? window.requestIdleCallback
        : (cb: IdleRequestCallback) => setTimeout(cb, 1);

    const handle = idleCallback(() => {
      setShouldLoad(true);
    });

    return () => {
      if ('cancelIdleCallback' in window && typeof handle === 'number') {
        window.cancelIdleCallback(handle);
      }
    };
  }, []);

  useEffect(() => {
    if (!shouldLoad) return;

    if (!MarqueeComponent) {
      import('react-fast-marquee').then((mod) => {
        MarqueeComponent = mod.default;
        setComponent(() => mod.default);
      });
    } else {
      setComponent(() => MarqueeComponent);
    }
  }, [shouldLoad]);

  if (!Component) {
    // Render a static version while loading
    return (
      <div
        className={props.className}
        style={{ ...props.style, overflow: 'hidden' }}
      >
        <div style={{ display: 'flex', gap: '1rem' }}>{props.children}</div>
      </div>
    );
  }

  return <Component {...props} />;
}
